/**
 * DOM helpers.
 */

import { GLOBAL_PAGE_WRAPPER_ID } from 'constants/ids';

import { is } from './helpers';

// Not using createElement, only checking if it exists.
// oxlint-disable-next-line typescript/unbound-method
const canUseDOM = is.func(globalThis.document?.createElement);

/**
 * Get the element that currently has focus, if any.
 */
export function getFocusedElement(): Element | null {
	const focused = document.activeElement;
	// If there is no candidate node, activeElement falls back to the body and
	// the document. Ignore those since they're never relevant when doing focus
	// management.
	// https://html.spec.whatwg.org/multipage/interaction.html#focus-management-apis
	return focused === document.body || focused instanceof Document
		? null
		: focused;
}

// Attributes and classes that hide an element from assistive tech.
const hiddenAttributes = ['hidden', 'aria-hidden'] as const;
const hiddenClasses = ['hidden', 'invisible'] as const;

// CSS selector for elements that can be focused or tabbed to.
//
// Based on https://github.com/KittyGiraudel/focusable-selectors.
// See also https://github.com/focus-trap/tabbable.
const notInert = ':not([inert]):not([inert] *)';
const notNegTabIndex = ':not([tabindex^="-"])';
const notDisabled = ':not(:disabled)';
// aria-hidden doesn't prevent focus but these utils are primarily used to find
// targets for sending focus to and aria-hidden elements are not desired there.
const notHidden = hiddenAttributes.map((attr) => `:not([${attr}])`).join('');
// Commenting out things that are very unlikely to be relevant in practice.
const focusableSelectorParts = [
	`a[href]${notInert}${notHidden}`,
	// `area[href]${notInert}${notHidden}`,
	`audio[controls]${notInert}${notHidden}`,
	`video[controls]${notInert}${notHidden}`,
	`button${notInert}${notHidden}${notDisabled}`,
	`select${notInert}${notHidden}${notDisabled}`,
	`textarea${notInert}${notHidden}${notDisabled}`,
	`input:not([type="hidden"])${notInert}${notHidden}${notDisabled}`,
	`details${notInert}${notHidden} > summary:first-of-type`,
	`iframe${notInert}${notHidden}`,
	// `embed${notInert}`,
	// `object${notInert}`,
	// `[contenteditable]${notInert}${notHidden}`,
	`[tabindex]${notInert}${notHidden}`,
];
const tabbableSelectorParts = focusableSelectorParts.map(
	(part) => `${part}${notNegTabIndex}`,
);
const focusableSelector = focusableSelectorParts.join(', ');
const tabbableSelector = tabbableSelectorParts.join(', ');

// CSS selector for elements that should give something meaningful to read
// for assistive technologies when focused.
const contentFocusTargetSelector = [
	focusableSelector,
	[
		'h1',
		'h2',
		'h3',
		'h4',
		'h5',
		'h6',
		'p',
		// Some things that technically have to be non-semantic elements like spans
		// can set this to be a viable target.
		'[role="paragraph"]',
		'img',
		'[role="img"]',
		'ol',
		'ul',
		'nav',
	]
		.map((el) => `${el}${notInert}${notHidden}`)
		.join(', '),
].join(', ');

/** Check if an element is hidden via attributes or classes. */
function isHidden(elem: HTMLElement) {
	return (
		hiddenAttributes.some((attr) => elem.hasAttribute(attr)) ||
		hiddenClasses.some((cls) => elem.classList.contains(cls))
	);
}

interface FocusableFilterOptions {
	checkClasses?: boolean;
	checkSize?: boolean;
}

/**
 * Filter a seemingly focusable element for things that makes it unfocusable.
 *
 * The `checkSize` option excludes elements that have a width and height of
 * zero pixels, which may be useful or wrong depending on the situation.
 *
 * - A zero sized element (e.g. width: 0; height: 0; overflow: hidden;) will
 *   be excluded but can still receive tab focus = could be bad.
 * - A child of a parent with display: none; will be excluded and can NOT
 *   recieve focus = probably good.
 *
 * A more correct approach would be checking for visibility up the parent tree,
 * but that would likely be extremely expensive. Instead consider if size
 * checking is needed on a case-by-case basis.
 */
function isFocusableFilter(
	elem: HTMLElement,
	{ checkClasses = true, checkSize = false }: FocusableFilterOptions = {},
) {
	if (
		checkClasses &&
		hiddenClasses.some((cls) => elem.classList.contains(cls))
	) {
		return false;
	}
	if (checkSize && elem.offsetHeight === 0 && elem.offsetWidth === 0) {
		return false;
	}
	return true;
}

/**
 * Select all focusable elements (can receive focus programmatically) contained
 * in the specified one.
 *
 * @see isFocusableFilter
 */
export function selectFocusable<T extends HTMLElement>(
	inElement: HTMLElement,
	opt?: FocusableFilterOptions,
): T[] {
	return [...inElement.querySelectorAll<T>(focusableSelector)].filter((el) =>
		isFocusableFilter(el, opt),
	);
}

/**
 * Select the first focusable element (can receive focus programmatically)
 * contained in the specified one.
 *
 * @see isFocusableFilter
 */
export function selectFirstFocusable<T extends HTMLElement>(
	inElement: HTMLElement,
	opt?: FocusableFilterOptions,
): T | undefined {
	// First from list intead of querySelector to handle matches being filtered
	// by isFocusableFilter.
	return selectFocusable<T>(inElement, opt)[0];
}

/**
 * Select all tabbable elements (can be reached with tab key navigation)
 * contained in the specified one.
 *
 * @see isFocusableFilter
 */
export function selectTabbable<T extends HTMLElement>(
	inElement: HTMLElement,
	opt?: FocusableFilterOptions,
): T[] {
	return [...inElement.querySelectorAll<T>(tabbableSelector)].filter((el) =>
		isFocusableFilter(el, opt),
	);
}

/**
 * Select the first tabbable element (can be reached with tab key navigation)
 * contained in the specified one.
 *
 * @see isFocusableFilter
 */
export function selectFirstTabbable<T extends HTMLElement>(
	inElement: HTMLElement,
	opt?: FocusableFilterOptions,
): T | undefined {
	// First from list intead of querySelector to handle matches being filtered
	// by isFocusableFilter.
	return selectTabbable<T>(inElement, opt)[0];
}

/**
 * Check if an element can be navigated to with the tab key.
 *
 * @see isFocusableFilter
 */
export function isTabbable(
	elem: HTMLElement,
	opt?: FocusableFilterOptions,
): boolean {
	return elem.matches(tabbableSelector) && isFocusableFilter(elem, opt);
}

/**
 * Check if an element can receive focus.
 *
 * @see isFocusableFilter
 */
export function isFocusable(
	elem: HTMLElement,
	opt?: FocusableFilterOptions,
): boolean {
	return elem.matches(focusableSelector) && isFocusableFilter(elem, opt);
}

/**
 * Find a 'viable content' target for next focus.
 *
 * Viable in this case means an element that a user can get moved to and
 * likely get something meaningful to read. Includes regular focusable
 * elements and also things like paragraphs, images and headings.
 */
export function findNextContentFocusTarget(
	from: HTMLElement,
): HTMLElement | null {
	let el = from.nextElementSibling as HTMLElement | null;
	while (el) {
		// No children of hidden elements are relevant.
		if (isHidden(el)) {
			el = el.nextElementSibling as HTMLElement | null;
			continue;
		}
		if (el.matches(contentFocusTargetSelector)) {
			return el;
		}
		const focusable = [
			...el.querySelectorAll<HTMLElement>(contentFocusTargetSelector),
		].find((e) => isFocusableFilter(e));
		if (focusable) {
			return focusable;
		}
		el = el.nextElementSibling as HTMLElement | null;
	}
	// No need to walk up any further than the global page wrapper.
	const parent = from.parentElement;
	if (!parent || parent.id === GLOBAL_PAGE_WRAPPER_ID) {
		return null;
	}
	return findNextContentFocusTarget(parent);
}

/**
 * Get the current window height, including scrollbar.
 */
function getWindowHeight() {
	return document.documentElement.clientHeight;
}

/**
 * Scroll to an element if it's outside the viewport.
 */
export function scrollIntoViewIfNeeded<T extends HTMLElement>(
	elem: T,
	{
		// Default to 95% intersection, some quick testing shows that 100% is not
		// always reached even if the element is fully visible.
		doneIntersectionThreshold = 0.95,
		onDone,
	}: { doneIntersectionThreshold?: number; onDone?: (elem: T) => void } = {},
) {
	// Element's top edge position relative to the viewport. Zero means the top
	// edge is right on the top of the current view.
	const { bottom: elemBottom, top: elemTop } = elem.getBoundingClientRect();
	// Not completely true but close enough - if CSS smooth scrolling is
	// supported by the browser, assume the options object for scrollIntoView is
	// too. Passing an object where not supported will just result in it being
	// interpreted as truthy rather than an error, which means the scrolling
	// will be 'top based'. More of a UX quirk than an actual problem.
	const supportsOptions = 'scrollBehavior' in document.documentElement.style;

	const observeDone = () => {
		if (onDone) {
			const observer = new IntersectionObserver(
				([entry]) => {
					if (entry?.isIntersecting) {
						onDone(entry.target as T);
						observer.disconnect();
					}
				},
				{ threshold: [doneIntersectionThreshold] },
			);
			observer.observe(elem);
		}
	};

	if (elemTop < 0) {
		observeDone();
		// Element is above the current viewport, scroll up to top alignment.
		elem.scrollIntoView(supportsOptions ? { behavior: 'smooth' } : true);
	} else if (elemBottom > getWindowHeight()) {
		observeDone();
		// Element is fully below the current viewport, scroll down to bottom
		// alignment.
		elem.scrollIntoView(
			supportsOptions ? { behavior: 'smooth', block: 'end' } : false,
		);
	}
}

/**
 * Scroll to the specified element, putting its top edge at the window's top.
 *
 * The default `header` offset value accounts for the fixed header. A custom
 * number can also be used.
 */
export function scrollTo(
	elem: HTMLElement,
	{
		offset = 'header',
		smooth = true,
	}: { offset?: 'header' | number; smooth?: boolean } = {},
) {
	// Using an approximate measurement for the fixed header height, it doesn't
	// have to be exact and hardcoding a number makes it much simpler.
	const topOffset = offset === 'header' ? -90 : offset;
	window.scrollTo({
		behavior: smooth ? 'smooth' : 'auto',
		top: elem.getBoundingClientRect().top + window.pageYOffset + topOffset,
	});
}

let scrollbarWidthCache: number | undefined;

/**
 * Get the width of scrollbars in the current browser. Only counts scrollbars
 * that affect layout, so overlay scrollbars have zero width.
 */
export function getScrollbarWidth(): number {
	// Do nothing on the server or if the body isn't ready yet.
	if (!canUseDOM || !document.body) {
		return 0;
	}

	if (is.number(scrollbarWidthCache)) {
		return scrollbarWidthCache;
	}

	const refSize = 100;
	const el = document.createElement('div');
	el.style.position = 'absolute';
	el.style.visibility = 'hidden';
	el.style.overflow = 'scroll';
	el.style.width = `${refSize}px`;
	el.style.height = `${refSize}px`;

	document.body.append(el);

	// clientWidth includes padding but excludes borders, margins, and
	// vertical scrollbars.
	scrollbarWidthCache = refSize - el.clientWidth;

	el.remove();

	return scrollbarWidthCache;
}

/**
 * Focus and (optionally) scroll to the specified element.
 */
export function goToElement(
	elementOrId: HTMLElement | string,
	{
		scroll = false,
		strict = false,
	}: {
		/** Scroll target element to the top of the window? */
		scroll?: boolean;
		/** Throw an error if there is no element for specified ID? */
		strict?: boolean;
	} = {},
) {
	const isId = is.string(elementOrId);
	const el = isId ? document.getElementById(elementOrId) : elementOrId;
	if (!el) {
		if (strict) {
			throw new Error(
				isId
					? `Missing target element with ID '${elementOrId}'`
					: 'Missing target element',
			);
		}
		return;
	}
	if (!isFocusableFilter(el)) {
		if (strict) {
			throw new Error('Target element is not focusable');
		}
		return;
	}
	if (!el.matches(focusableSelector)) {
		el.tabIndex = -1;
	}
	if (scroll) {
		scrollTo(el);
	}
	el.focus();
}

/**
 * Focus the first focusable child of target element, or the target itself if
 * no focusable children are found.
 */
export function goToElementFocusableOrRoot(element: HTMLElement) {
	const focusables = selectFocusable(element);
	if (focusables[0]) {
		focusables[0].focus();
	} else {
		goToElement(element);
	}
}
